// Green Light Tune — high-conversion landing page for the remote tuning service.
// Standalone route at /green-light. NOT the homepage — this is a paid-traffic funnel.
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2, XCircle, ArrowRight, UploadCloud, Sparkles, Zap,
  ShieldCheck, Clock, Award, MessageSquare, FileText, Gauge,
  TrendingUp, Cpu, Wrench, AlertTriangle, ChevronDown, Plus,
  Star, BadgeCheck, Lock, Activity, Loader2,
} from 'lucide-react';
import ModOptionsDialog from './ModOptionsDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FADE_UP = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const HERO_BG = '/brand/ai-tuner-pro-logo.jpg';

// Map of friendly CTA keys → Stripe TUNING_PACKAGES ids (server.py)
const PACKAGE_IDS = {
  single: 'single_tune',
  dual: 'dual_tune',
  shop: 'shop_pack_10',
  combo_wifi: 'datalogger_combo_wifi',
  combo_lte: 'datalogger_combo_lte',
  priority: 'priority_addon',
  autotuner: 'autotuner_one',
  datalogger_alone: 'datalogger_alone',
  datalogger_alone_lte: 'datalogger_alone_lte',
  datalogger_tune: 'datalogger_tune_bundle',
  datalogger_tune_lte: 'datalogger_tune_bundle_lte',
  ultimate: 'ultimate_bundle',
  ultimate_lte: 'ultimate_bundle_lte',
  megasquirt: 'megasquirt_tune',
};

// =================== HERO ===================
function GreenLightHero({ onCTA }) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0A0A0A]">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]" />
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,122,255,0.04) 0px, rgba(0,122,255,0.04) 1px, transparent 1px, transparent 3px)' }} />
      </div>

      <motion.div
        className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full bg-[#FF5E1A]/15 blur-[140px]"
        animate={{ opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 6, repeat: Infinity }}
      />

      <div className="relative z-10 container mx-auto px-6 py-20">
        <motion.div {...FADE_UP} className="max-w-4xl">
          {/* Top badge — keep the actual green dot since that IS the brand "Green Light" promise */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 rounded-sm">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-[#FF5E1A]">Green Light Tune Program</span>
          </div>

          <h1 className="font-['Outfit'] text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white mb-6">
            Pay for <span className="bg-gradient-to-r from-[#FF5E1A] to-[#FF7A42] bg-clip-text text-transparent">results</span>.<br />
            Not attempts.
          </h1>

          <p className="text-lg sm:text-xl text-zinc-300 max-w-2xl mb-4 leading-relaxed">
            One price. <span className="text-white font-semibold">No per-flash fees.</span> No per-revision charges.
            We tune your ECU remotely — base tune, datalog refinement, final delivery — until your car gets the
            <span className="text-[#FF5E1A] font-bold"> Green Light</span>.
          </p>
          <p className="text-base text-zinc-400 max-w-2xl mb-10">
            Faster than local shops. Data-driven. AI-assisted. Built by tuners, for tuners.
          </p>

          <div className="flex flex-wrap gap-4 items-center mb-10">
            <button
              onClick={onCTA}
              data-testid="hero-greenlight-cta"
              className="group relative px-8 py-4 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white font-bold text-sm tracking-[0.12em] uppercase rounded-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_50px_rgba(255,94,26,0.5)] flex items-center gap-2"
            >
              <UploadCloud className="w-4 h-4" />
              Start Your Tune — Upload File
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href="#process"
              className="text-sm text-zinc-400 hover:text-white font-['JetBrains_Mono'] uppercase tracking-wider transition-colors flex items-center gap-2"
            >
              See how it works
              <ChevronDown className="w-4 h-4" />
            </a>
          </div>

          {/* Trust strip */}
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-zinc-500">
            <span className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5 text-[#007AFF]" />Stock backup before flash</span>
            <span className="text-zinc-800 hidden sm:inline">•</span>
            <span className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-[#007AFF]" />0 bricked ECUs</span>
            <span className="text-zinc-800 hidden sm:inline">•</span>
            <span className="flex items-center gap-2"><Award className="w-3.5 h-3.5 text-[#007AFF]" />Datalog-verified gains</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// =================== VALUE PROPS ===================
function ValueProps() {
  const props = [
    { icon: ShieldCheck, title: 'No per-flash fees', text: 'Flash as many times as the revision plan allows. Zero hidden costs.' },
    { icon: Zap,         title: 'Fast turnaround',   text: 'Base tune in 24–48h. Most cars hit Green Light inside a week.' },
    { icon: Activity,    title: 'Data-driven',        text: 'Every revision is built from YOUR datalogs, not a template.' },
    { icon: Cpu,         title: 'AI-assisted',        text: 'Hybrid AI + master tuner workflow catches issues humans miss.' },
    { icon: MessageSquare, title: 'Direct chat',     text: 'Real-time chat with your tuner. No support tickets, no waiting.' },
    { icon: BadgeCheck,  title: 'Green Light backed', text: 'We tune until the car is right — within defined safety limits.' },
  ];
  return (
    <section className="py-20 bg-[#0A0A0A] border-t border-[#18181B]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-5">
          {props.map((p) => (
            <div key={p.title} className="p-4 sm:p-5 bg-[#121212] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/30 transition-colors">
              <p.icon className="w-5 h-5 text-[#FF5E1A] mb-3" />
              <div className="font-['Outfit'] text-sm font-bold text-white mb-1.5">{p.title}</div>
              <p className="text-xs text-zinc-400 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// =================== PROCESS ===================
function Process({ onCTA }) {
  const steps = [
    { num: '01', icon: UploadCloud,   title: 'Upload Stock File',
      text: 'Drag your ECU binary into the portal — or plug in your AutoTuner / KESS / BTT and we pull it.',
      time: 'Day 0' },
    { num: '02', icon: Sparkles, title: 'Base Tune Built',
      text: 'AI + master tuner generate a base calibration from your fuel + hardware + goals. 24–48h.',
      time: 'Day 1–2' },
    { num: '03', icon: Activity, title: 'You Datalog It',
      text: 'Flash, drive a structured datalog (idle + part-throttle + WOT). Upload back via the portal.',
      time: 'Day 2–4' },
    { num: '04', icon: TrendingUp, title: 'Revisions (2–3 incl)',
      text: 'We refine fuel, timing, boost, knock control from real data. Up to 3 revisions included.',
      time: 'Day 4–6' },
    { num: '05', icon: BadgeCheck, title: 'Green Light Tune',
      text: 'Final calibration delivered. AFR, knock, IAT, LTFT all within target. The car is yours.',
      time: 'Day 6–7' },
  ];
  return (
    <section id="process" className="py-24 bg-gradient-to-b from-[#0A0A0A] via-[#050505] to-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mb-12">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">// The Process</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
            Five steps. One week.<br />
            <span className="text-[#FF5E1A]">Green Light delivered.</span>
          </h2>
        </motion.div>

        <div className="space-y-3">
          {steps.map((s, i) => (
            <motion.div
              key={s.num}
              {...FADE_UP}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="group flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 sm:p-6 bg-[#121212] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/40 transition-all"
              data-testid={`process-step-${s.num}`}
            >
              <div className="flex-shrink-0 flex items-center gap-4">
                <div className="font-['JetBrains_Mono'] text-3xl font-black text-zinc-700 group-hover:text-[#FF5E1A]/40 transition-colors">{s.num}</div>
                <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 group-hover:bg-[#FF5E1A]/20 transition-colors">
                  <s.icon className="w-5 h-5 text-[#FF5E1A]" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1.5">
                  <h3 className="font-['Outfit'] text-lg sm:text-xl font-bold text-white">{s.title}</h3>
                  <span className="hidden sm:inline-flex text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider px-2 py-0.5 bg-[#0A0A0A] border border-[#27272A] text-zinc-500 rounded-sm">{s.time}</span>
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div {...FADE_UP} className="text-center mt-10">
          <button
            onClick={onCTA}
            data-testid="process-cta"
            className="inline-flex items-center gap-2 px-7 py-3 bg-[#FF5E1A]None hover:bg-[#FF5E1A]None text-[#0A0A0A] font-bold text-sm tracking-[0.12em] uppercase rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_0_35px_rgba(255,94,26,0.5)]"
          >
            Upload Your File <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

// =================== PRICING ===================
// Tier card with optional WiFi / LTE variant toggle (for datalogger-containing bundles).
function TierCard({ tier: t, checkoutLoading, onCheckout, onFallback, atOneInventory }) {
  const hasVariants = !!t.variants;
  const [variant, setVariant] = useState(t.featured ? 'wifi' : 'wifi');
  const active = hasVariants ? t.variants[variant] : t;
  const packageKey = active.packageKey;
  const price = active.price || t.price;
  const cta = active.cta || t.cta;

// Inventory badge — only show on cards that include the AI Tuner Pro Flasher hardware
  const includesAtOne = packageKey === 'autotuner' || packageKey === 'ultimate' || packageKey === 'ultimate_lte';
  const inventoryRemaining = includesAtOne ? atOneInventory : null;
  const lowStock = inventoryRemaining !== null && inventoryRemaining > 0 && inventoryRemaining <= 5;
  const outOfStock = inventoryRemaining === 0;

  const handleClick = () => {
    if (packageKey) onCheckout(packageKey);
    else onFallback(t);
  };

  return (
    <motion.div
      {...FADE_UP}
      className={`relative p-6 sm:p-7 rounded-sm border transition-all hover:-translate-y-1 flex flex-col ${
        t.featured
          ? 'bg-gradient-to-b from-[#FF5E1A]/10 via-[#121212] to-[#121212] border-[#FF5E1A]/50 shadow-[0_0_50px_rgba(255,94,26,0.2)] lg:scale-[1.03]'
          : 'bg-[#121212] border-[#27272A]'
      }`}
      data-testid={`pricing-${t.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
    >
      {t.featured && (
        <div className="absolute -top-3 left-6 sm:left-7 px-2.5 py-0.5 bg-[#FF5E1A] text-[#0A0A0A] text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] rounded-sm font-bold">
          Most Popular
        </div>
      )}
      {t.shipBadge && (
        <div className="absolute -top-3 right-6 sm:right-7 px-2.5 py-0.5 bg-[#0A0A0A] text-zinc-300 text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] rounded-sm font-bold border border-[#27272A]">
          Ships {t.shipBadge}
        </div>
      )}
      <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-2">{t.tag}</div>
      <h3 className="font-['Outfit'] text-2xl font-black text-white mb-2">{t.name}</h3>

      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-['Outfit'] text-4xl font-black text-white">{price}</span>
      </div>
      {t.sub ? (
        <div className="text-xs text-zinc-500 mb-4 font-['JetBrains_Mono']">{t.sub}</div>
      ) : (
        <div className="mb-4" />
      )}

      {hasVariants && (
        <div
          role="radiogroup"
          aria-label={`${t.name} connectivity`}
          className="inline-flex mb-5 rounded-sm border border-[#27272A] bg-[#0A0A0A] p-0.5 self-start"
          data-testid={`variant-toggle-${t.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
        >
          {['wifi', 'lte'].map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={variant === v}
              onClick={() => setVariant(v)}
              data-testid={`variant-${v}-${t.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              className={`px-3 py-1 text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] rounded-sm transition-all ${
                variant === v
                  ? 'bg-[#FF5E1A] text-[#0A0A0A] font-bold'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              {v === 'wifi' ? 'WiFi' : 'LTE +$100'}
            </button>
          ))}
        </div>
      )}

      <ul className="space-y-2.5 mb-6 flex-1">
        {t.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm text-zinc-300">
            <CheckCircle2 className="w-4 h-4 text-[#FF5E1A] flex-shrink-0 mt-0.5" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {includesAtOne && inventoryRemaining !== null && (
        <div
          className={`mb-3 px-3 py-2 rounded-sm border text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] flex items-center justify-between ${
            outOfStock
              ? 'bg-red-500/10 border-red-500/30 text-red-300'
              : lowStock
                ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                : 'bg-emerald-500/5 border-emerald-500/30 text-emerald-300'
          }`}
          data-testid={`at-one-stock-${packageKey}`}
        >
          <span>{outOfStock ? 'Sold out — back-order' : `${inventoryRemaining} units left`}</span>
          <span className="opacity-70">first batch</span>
        </div>
      )}

      <button
        onClick={handleClick}
        disabled={(!!packageKey && checkoutLoading === packageKey) || outOfStock}
        data-testid={`pricing-cta-${packageKey || t.name.toLowerCase()}`}
        className={`w-full py-3 rounded-sm font-bold text-[11px] sm:text-xs tracking-[0.12em] uppercase transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
          t.featured
            ? 'bg-[#FF5E1A] hover:bg-[#FF7A42] text-white hover:shadow-[0_0_30px_rgba(255,94,26,0.5)]'
            : 'bg-[#27272A] hover:bg-[#FF5E1A]/20 hover:border-[#FF5E1A]/40 text-white border border-[#27272A]'
        }`}
      >
        {checkoutLoading && checkoutLoading === packageKey ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
        ) : outOfStock ? (
          'Back-order — email us'
        ) : (
          cta
        )}
      </button>
    </motion.div>
  );
}

function Pricing({ onCTA, onCheckout, checkoutLoading }) {
  const [atOneInventory, setAtOneInventory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/inventory/at-one`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (!cancelled && data) setAtOneInventory(data.remaining ?? null); })
      .catch(() => { /* fall back to no inventory display */ });
    return () => { cancelled = true; };
  }, []);

  const tuneTiers = [
    {
      name: 'Single Tune',
      tag: 'One car · one calibration',
      price: '$250',
      featured: false,
      packageKey: 'single',
      bullets: [
        'One custom tune · one vehicle',
        'Unlimited revisions until YOU Green Light it',
        'AI Copilot + Tune Scorecard + Health Gate',
        'Stock backup archived forever',
        'Free ECU recovery during our process',
        'Fuel: pump gas / E85 / flex',
      ],
      cta: 'Start Single Tune — $250',
    },
    {
      name: 'Dual Tune',
      tag: 'Same car · 2 calibrations',
      price: '$450',
      sub: 'saves $50 vs two singles',
      featured: true,
      packageKey: 'dual',
      bullets: [
        'Two tunes for the same VIN',
        'E.g. daily pump-gas + track E85',
        'Or Stage 1 now + Stage 2 when mods land',
        'Unlimited revisions until Green Light on each',
        'Free ECU recovery during our process',
        'Best value for daily-driver tuners',
      ],
      cta: 'Start Dual Tune — $450',
    },
    {
      name: 'Shop Pack',
      tag: 'Professional / multi-car',
      price: '$1,500',
      sub: '10 tunes · $150 each',
      featured: false,
      packageKey: 'shop',
      bullets: [
        '10 tunes · use across different vehicles',
        'Bulk pricing: $150/tune vs $250 single',
        '12-month validity from first use',
        'Unlimited revisions until Green Light on each',
        'Free ECU recovery during our process',
        'Built for shops + pro tuners',
      ],
      cta: 'Start Shop Pack — $1,500',
    },
  ];

  const hardwareTiers = [
    {
      name: 'Smart Datalogger',
      tag: 'Standalone · hardware',
      sub: 'no tune included',
      featured: false,
      variants: {
        wifi: { price: '$199', packageKey: 'datalogger_alone',     cta: 'Add Datalogger — $199' },
        lte:  { price: '$299', packageKey: 'datalogger_alone_lte', cta: 'Add Datalogger LTE — $299' },
      },
      bullets: [
        'Smart Datalogger device',
        'Every drive auto-uploads to your portal',
        'AI Copilot scores every pull',
        'Baseline your car before tuning',
        'Pair with any tune later',
      ],
    },
    {
      name: 'AI Tuner Pro Flasher',
      tag: 'OBD flasher · ships 2–3 weeks',
      price: '$499',
      sub: 'our branded tune flasher',
      featured: false,
      packageKey: 'autotuner',
      shipBadge: '2–3 wk',
      bullets: [
        'AI Tuner Pro Flasher OBD hardware',
        'Pre-paired to your portal account',
        'Tune files staged & ready on arrival',
        'Flashes via the companion mobile app',
        'Some steps still manually coordinated',
        'Protective case + free shipping',
      ],
      cta: 'Get AI Tuner Pro Flasher — $499',
    },
    {
      name: 'Datalogger + Tune',
      tag: 'Single tune + Smart Datalogger',
      sub: 'saves $24 vs separate',
      featured: true,
      variants: {
        wifi: { price: '$425', packageKey: 'datalogger_tune',     cta: 'Start Bundle — $425' },
        lte:  { price: '$525', packageKey: 'datalogger_tune_lte', cta: 'Start Bundle LTE — $525' },
      },
      bullets: [
        'Smart Datalogger + 1 custom tune',
        'Every drive auto-uploads',
        'AI scores every pull automatically',
        'Next revision queued before you park',
        'Unlimited revisions until Green Light',
        'Free ECU recovery during our process',
      ],
    },
    {
      name: 'Ultimate Bundle',
      tag: 'Datalogger + 2 tunes + Flasher',
      sub: 'saves $473 · ships 2–3 weeks',
      featured: false,
      shipBadge: '2–3 wk',
      variants: {
        wifi: { price: '$675', packageKey: 'ultimate',     cta: 'Go Ultimate — $675' },
        lte:  { price: '$775', packageKey: 'ultimate_lte', cta: 'Go Ultimate LTE — $775' },
      },
      bullets: [
        'Smart Datalogger',
        '2 custom tunes (same VIN)',
        'AI Tuner Pro Flasher included',
        'Unlimited revisions on each tune',
        'Saves $473 vs buying separately',
        'Flasher ships in 2–3 weeks',
      ],
    },
  ];

  const upsells = [
    { icon: Zap, title: 'Priority Queue', price: '+$100', text: 'Front of the queue. Base tune in 24h, revisions same day.', packageKey: 'priority' },
  ];

  const handleTierClick = (tier) => {
    if (tier.packageKey) {
      onCheckout(tier.packageKey);
    } else {
      window.location.href = 'mailto:support@aitunerproportal.com?subject=Custom%20Tune%20Quote%20Request';
    }
  };

  const renderTierCard = (t) => <TierCard key={t.name} tier={t} checkoutLoading={checkoutLoading} onCheckout={onCheckout} onFallback={handleTierClick} atOneInventory={atOneInventory} />;

  return (
    <section id="pricing" className="py-24 bg-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-2xl mx-auto text-center mb-8">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">// Pricing</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
            One price. All revisions included.
          </h2>
          <p className="text-lg text-zinc-400">Pick your stage. We do the rest until your car is dialed.</p>
        </motion.div>

        {/* ---------- MegaSquirt Banner (big, near top) ---------- */}
        <motion.a
          href="#megasquirt"
          {...FADE_UP}
          data-testid="megasquirt-top-banner-greenlight"
          className="group block relative overflow-hidden mb-12 max-w-5xl mx-auto rounded-sm border border-emerald-500/40 bg-gradient-to-r from-emerald-500/15 via-emerald-500/5 to-emerald-500/15 hover:border-emerald-400/70 hover:from-emerald-500/25 hover:via-emerald-500/10 transition-all"
        >
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-[15%] -translate-y-1/2 w-[320px] h-[320px] rounded-full bg-emerald-500/20 blur-[120px]" />
            <div className="absolute top-1/2 right-[10%] -translate-y-1/2 w-[280px] h-[280px] rounded-full bg-emerald-500/15 blur-[100px]" />
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 p-5 sm:p-6">
            <div className="flex-shrink-0 flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_12px_rgba(52,211,153,0.8)]" />
              <span className="text-[10px] sm:text-[11px] font-['JetBrains_Mono'] uppercase tracking-[0.25em] text-emerald-300 font-bold">
                Open-ECU pricing
              </span>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="font-['Outfit'] text-xl sm:text-2xl lg:text-3xl font-black tracking-tight text-white leading-tight">
                MegaSquirt users get tunes for{' '}
                <span className="bg-gradient-to-r from-emerald-300 via-emerald-200 to-emerald-100 bg-clip-text text-transparent">$50</span>.
              </div>
              <div className="text-[11px] sm:text-xs text-zinc-400 mt-1">
                MS1 · MS2 · MS3 · MS3X · MicroSquirt — unlimited revisions, same AI Copilot, 80% off.
              </div>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-[#0A0A0A] text-xs font-bold tracking-[0.12em] uppercase rounded-sm group-hover:bg-emerald-400 group-hover:shadow-[0_0_28px_rgba(52,211,153,0.5)] transition-all">
                See Details <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </div>
          </div>
        </motion.a>

        {/* ---------- Tune Services ---------- */}
        <div className="mb-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-[#27272A]" />
          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.25em] text-zinc-500">// Tune Services</span>
          <div className="h-px flex-1 bg-[#27272A]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
          {tuneTiers.map(renderTierCard)}
        </div>

        {/* ---------- Hardware & Bundles ---------- */}
        <div className="mb-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-[#27272A]" />
          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.25em] text-zinc-500">// Hardware &amp; Bundles</span>
          <div className="h-px flex-1 bg-[#27272A]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {hardwareTiers.map(renderTierCard)}
        </div>

        {/* ---------- Upsells ---------- */}
        <motion.div {...FADE_UP} className="max-w-5xl mx-auto">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-zinc-500 mb-4 text-center">// Optional add-ons</div>
          <div className="grid grid-cols-1 gap-3 max-w-xl mx-auto">
            {upsells.map((u) => (
              <button
                key={u.title}
                type="button"
                onClick={() => onCheckout(u.packageKey)}
                disabled={checkoutLoading === u.packageKey}
                data-testid={`upsell-cta-${u.packageKey}`}
                className="text-left flex items-start gap-3 p-4 bg-[#121212] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/50 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-wait"
              >
                <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-sm bg-[#FF5E1A]/10 border border-[#FF5E1A]/30">
                  {checkoutLoading === u.packageKey
                    ? <Loader2 className="w-4 h-4 text-[#FF5E1A] animate-spin" />
                    : <u.icon className="w-4 h-4 text-[#FF5E1A]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <div className="font-['Outfit'] text-sm font-bold text-white truncate">{u.title}</div>
                    <span className="text-[#FF5E1A] font-['JetBrains_Mono'] text-xs font-bold">{u.price}</span>
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">{u.text}</p>
                </div>
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// =================== GREEN LIGHT GUARANTEE ===================
function Guarantee() {
  const promises = [
    'Unlimited revisions until YOU Green Light the tune — never a per-revision fee',
    'Free ECU recovery if anything goes sideways during our process',
    'We tune until the vehicle is correct within defined limits',
    'Stock backup archived in our system — instant rollback',
    'Direct chat with your tuner — no support tickets',
    'AFR / knock / IAT / LTFT all within published targets',
    'Clear written expectations from day one',
    'No hidden fees per flash or per revision',
  ];
  return (
    <section className="py-24 bg-gradient-to-b from-[#0A0A0A] via-[#050505] to-[#0A0A0A] relative overflow-hidden">
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#FF5E1A]/8 blur-[140px]"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 6, repeat: Infinity }}
      />
      <div className="relative container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 rounded-sm">
              <BadgeCheck className="w-3.5 h-3.5 text-[#FF5E1A]" />
              <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-[#FF5E1A]">Green Light Guarantee</span>
            </div>
            <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
              We don't stop until <span className="text-[#FF5E1A]">the car is right.</span>
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              The Green Light Guarantee protects you AND us. Clear targets, transparent process, fair limits.
            </p>
          </div>

          <div className="bg-[#121212] border border-[#FF5E1A]/30 rounded-sm p-7 sm:p-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {promises.map((p) => (
                <div key={p} className="flex items-start gap-3" data-testid={`guarantee-item-${p.slice(0,20).replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
                  <CheckCircle2 className="w-5 h-5 text-[#FF5E1A] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-zinc-200">{p}</span>
                </div>
              ))}
            </div>
            <div className="mt-7 pt-6 border-t border-[#27272A] text-xs text-zinc-500 font-['JetBrains_Mono'] leading-relaxed">
              "Defined limits" = within ±0.3 AFR of target across the load/RPM map, knock voltage below platform threshold, LTFT |%| ≤ 5 at steady cruise, IAT-corrected timing within 2° of safe ceiling. Mechanical issues (compression, leaks, sensors out of spec) fall outside the guarantee — we'll diagnose, but you fix the hardware first.
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// =================== COMPARISON ===================
function Comparison() {
  const rows = [
    { feature: 'Pricing model',          us: 'One price, fully inclusive', them: 'Per-visit + per-flash + per-revision' },
    { feature: 'Revisions',              us: '2–3 included (more available)', them: 'Each one billed separately' },
    { feature: 'Turnaround',             us: 'Base in 24–48h, full week to Green Light', them: '2–6 weeks of appointments' },
    { feature: 'Where',                  us: 'Anywhere — uploads + datalogs', them: 'Drive to the shop, repeatedly' },
    { feature: 'Process',                us: 'Data-driven AI + master tuner review', them: 'One tuner, gut-feel iteration' },
    { feature: 'Communication',          us: 'Real-time chat in the portal', them: '"I\'ll call you back next week"' },
    { feature: 'Stock backup',           us: 'Archived in our cloud forever', them: 'On a USB drive, somewhere' },
    { feature: 'Hidden fees',            us: 'None', them: 'Diag fee, dyno fee, retune fee, rush fee' },
  ];
  return (
    <section className="py-24 bg-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-2xl mx-auto text-center mb-12">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">// Why Switch</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white">
            Local shop vs. AI Tuner Pro
          </h2>
        </motion.div>

        <motion.div {...FADE_UP} className="max-w-5xl mx-auto overflow-hidden border border-[#27272A] rounded-sm">
          <div className="grid grid-cols-[1.4fr_1fr_1fr] bg-[#0A0A0A] border-b border-[#27272A]">
            <div className="p-4 text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-zinc-500">Capability</div>
            <div className="p-4 text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] border-l border-[#27272A]">AI Tuner Pro</div>
            <div className="p-4 text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-zinc-600 border-l border-[#27272A]">Traditional shop</div>
          </div>
          {rows.map((r, i) => (
            <div key={r.feature} className={`grid grid-cols-[1.4fr_1fr_1fr] ${i % 2 === 0 ? 'bg-[#121212]' : 'bg-[#0F0F0F]'} ${i < rows.length - 1 ? 'border-b border-[#27272A]' : ''}`}>
              <div className="p-4 text-sm text-white font-medium">{r.feature}</div>
              <div className="p-4 border-l border-[#27272A] flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-[#FF5E1A] flex-shrink-0 mt-0.5" />
                <span className="text-zinc-200">{r.us}</span>
              </div>
              <div className="p-4 border-l border-[#27272A] flex items-start gap-2 text-sm">
                <XCircle className="w-4 h-4 text-zinc-700 flex-shrink-0 mt-0.5" />
                <span className="text-zinc-500">{r.them}</span>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// =================== FAQ ===================
function FAQ() {
  const [open, setOpen] = useState(0);
  const items = [
    {
      q: 'What if my car needs more revisions than included?',
      a: 'You can buy additional revisions à la carte ($75 each), or grab the Unlimited Revisions add-on (+$200) for total peace of mind. Most cars hit Green Light inside the included 3 — the extra is rare.',
    },
    {
      q: 'How long does the Green Light process take?',
      a: 'Base tune in 24–48h. Most customers reach final Green Light within 5–7 days, gated by how fast you can drive datalogs and report back. Priority Tuning (+$100) typically halves that.',
    },
    {
      q: 'What vehicles are supported?',
      a: 'BMW (E-chassis through G-chassis: N51, N52, N54, N55, S55, B58, S58, S63), Audi/VW (B7-B9 / MQB-Evo, MED17/MEVD17/MG1CS family), Mercedes AMG, Porsche, GM LS/LT/LSx, Ford EcoBoost + Coyote, plus diesel platforms (Cummins, Duramax, Powerstroke). For unknown ECUs, our AI Discovery Mode reasons from first principles. If we can\'t do your car, we tell you up front and refund any deposit.',
    },
    {
      q: 'Do I need special hardware?',
      a: 'You need a flashing tool. The easiest path is the AI Tuner Pro Flasher ($499, plug-and-flash, customer-friendly, included in the Ultimate Bundle). Also compatible: KESS V2, MPPS, BTT for older BMW, or HP Tuners for GM. We can recommend the right tool for your car — see add-ons.',
    },
    {
      q: 'How do I send you datalogs?',
      a: 'BimmerLink + Veepeak BLE+ (~$25) for BMW. HP Tuners VCM Scanner for GM. Cobb AccessPort for Subaru/Ford. Tool32 if you have a K+DCAN cable. Drag the CSV into the portal — our AI auto-analyzes before your tuner even opens it.',
    },
    {
      q: 'What if you brick my ECU?',
      a: 'In 5 years and thousands of tunes, zero bricks. Stock backup is archived the moment you upload. If a flash ever fails, we walk you through recovery and cover any tool/dealer cost up to $500. (Mechanical issues outside our control are excluded — see Green Light Guarantee.)',
    },
    {
      q: 'Can I get a refund if I\'m not satisfied?',
      a: 'Full refund any time before we deliver the base tune. After base tune is delivered, you have 14 days to use your included revisions; refunds at that stage are pro-rated. The Green Light Guarantee means we keep working if the targets aren\'t hit — no refund needed.',
    },
  ];
  return (
    <section className="py-24 bg-gradient-to-b from-[#0A0A0A] via-[#050505] to-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">// FAQ</div>
            <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white">
              Real questions. Real answers.
            </h2>
          </div>

          <div className="space-y-2">
            {items.map((it, i) => (
              <div
                key={it.q}
                className={`border rounded-sm transition-colors ${open === i ? 'border-[#FF5E1A]/40 bg-[#121212]' : 'border-[#27272A] bg-[#0F0F0F] hover:border-zinc-700'}`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  className="w-full p-5 flex items-start justify-between gap-4 text-left"
                  onClick={() => setOpen(open === i ? -1 : i)}
                >
                  <span className="font-['Outfit'] text-base sm:text-lg font-bold text-white">{it.q}</span>
                  <Plus className={`w-5 h-5 flex-shrink-0 mt-0.5 transition-transform ${open === i ? 'rotate-45 text-[#FF5E1A]' : 'text-zinc-500'}`} />
                </button>
                {open === i && (
                  <div className="px-5 pb-5 text-sm text-zinc-300 leading-relaxed">{it.a}</div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// =================== FINAL CTA ===================
function FinalCTA({ onCTA }) {
  return (
    <section className="py-24 bg-[#0A0A0A] relative overflow-hidden">
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[#FF5E1A]/12 blur-[160px]"
        animate={{ opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 5, repeat: Infinity }}
      />
      <div className="relative container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mx-auto text-center">
          <BadgeCheck className="w-12 h-12 text-[#FF5E1A] mx-auto mb-6" />
          <h2 className="font-['Outfit'] text-4xl sm:text-6xl font-black tracking-tighter text-white mb-5 leading-[1.05]">
            Ready to <span className="text-[#FF5E1A]">Get Green Lighted?</span>
          </h2>
          <p className="text-lg text-zinc-300 mb-10 max-w-xl mx-auto">
            One price. Every revision included. Your car dialed within a week. Upload your file and let's go.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={onCTA}
              data-testid="final-cta-upload"
              className="group px-8 py-4 bg-[#FF5E1A]None hover:bg-[#FF5E1A]None text-[#0A0A0A] font-bold text-sm tracking-[0.12em] uppercase rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_0_50px_rgba(255,94,26,0.6)] flex items-center gap-2"
            >
              <UploadCloud className="w-4 h-4" />
              Start Your Tune
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href="#pricing"
              className="px-8 py-4 border border-[#27272A] hover:border-[#FF5E1A]/50 text-white font-bold text-sm tracking-[0.12em] uppercase rounded-sm transition-all hover:bg-[#FF5E1A]/5"
            >
              See Pricing
            </a>
          </div>
          <p className="text-xs text-zinc-600 font-['JetBrains_Mono'] uppercase tracking-wider mt-6">
            Stripe Checkout • Secure • Instant Portal Access
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// =================== FOOTER ===================
function GLFooter() {
  return (
    <footer className="border-t border-[#18181B] py-10 bg-[#050505]">
      <div className="container mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-zinc-500 font-['JetBrains_Mono']">
        <div>© 2026 AI Tuner Pro · Green Light Tune Program</div>
        <div className="flex gap-5 uppercase tracking-wider">
          <a href="/" className="hover:text-white transition-colors">Home</a>
          <a href="#process" className="hover:text-white transition-colors">Process</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          <a href="mailto:support@aitunerproportal.com" className="hover:text-white transition-colors">Contact</a>
        </div>
      </div>
    </footer>
  );
}

// =================== MEGASQUIRT COMMUNITY SECTION ===================
// Special $50 pricing for the MegaSquirt / open-ECU crowd. They send us a .msq
// file (which is plain XML), we tune it cell-by-cell against their TunerStudio
// CSV datalogs, and we don't have to do binary-locating work — so the savings
// get passed straight through to the community.
function MegaSquirtSection({ onCheckout, checkoutLoading }) {
  const bullets = [
    'MS1 / MS2 / MS3 / MS3X / MicroSquirt all welcome',
    'Send your .msq file (or stock TunerStudio project)',
    'TunerStudio CSV datalogs scored by AI Copilot',
    'Same Green Light loop — unlimited revisions until YOU approve',
    'Cell-by-cell delta on your VE / spark / AFR tables',
    'Stock backup of your .msq archived forever',
  ];
  const isLoading = checkoutLoading === 'megasquirt';

  return (
    <section
      id="megasquirt"
      className="py-24 bg-gradient-to-b from-[#0A0A0A] via-[#0F0E12] to-[#0A0A0A] border-t border-[#27272A] relative overflow-hidden"
      data-testid="megasquirt-section"
    >
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-emerald-500/10 blur-[160px] pointer-events-none" />

      <div className="container mx-auto px-6 relative">
        <div className="max-w-5xl mx-auto">
          <motion.div {...FADE_UP} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/40 rounded-sm mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.22em] text-emerald-300">
                MegaSquirt Community Pricing
              </span>
            </div>
            <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
              MegaSquirt users get tunes for{' '}
              <span className="bg-gradient-to-r from-emerald-300 via-emerald-200 to-emerald-100 bg-clip-text text-transparent">
                $50.
              </span>
            </h2>
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              The .msq format is open. TunerStudio is open. The community has earned a discount,
              not a markup. Same AI Copilot. Same unlimited revisions. Same Green Light loop.
              Just $50.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left — pitch + bullets */}
            <motion.div
              {...FADE_UP}
              className="lg:col-span-3 p-6 sm:p-8 bg-[#0F0F12] border border-[#27272A] rounded-sm"
            >
              <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-emerald-300 mb-2">
                // Why so cheap?
              </div>
              <h3 className="font-['Outfit'] text-2xl font-bold text-white mb-3">
                Open ECUs deserve open pricing.
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                Most of the cost of a custom tune is locating maps inside a locked OEM binary.
                MegaSquirt skips that — your .msq is plain XML with named tables. We can read it
                in 3 seconds. So we charge what the work actually costs, not what the binary
                puzzle would cost.
              </p>
              <ul className="space-y-2.5 mb-6">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-zinc-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-zinc-500 font-['JetBrains_Mono'] leading-relaxed">
                Note: MegaSquirt tunes don&apos;t need a flashing tool — TunerStudio writes
                directly to your ECU over USB. So no AI Tuner Pro Flasher needed and no
                Smart Datalogger needed (TunerStudio already logs). $50 covers the whole job.
              </p>
            </motion.div>

            {/* Right — CTA card */}
            <motion.div
              {...FADE_UP}
              className="lg:col-span-2 p-6 sm:p-8 bg-gradient-to-br from-emerald-500/10 via-[#0A0A0A] to-[#0A0A0A] border border-emerald-500/40 rounded-sm flex flex-col"
              data-testid="megasquirt-cta-card"
            >
              <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-emerald-300 mb-2">
                MegaSquirt tune
              </div>
              <h3 className="font-['Outfit'] text-2xl font-bold text-white mb-2">
                Community Tune
              </h3>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-['Outfit'] text-5xl font-black text-white">$50</span>
                <span className="text-sm text-zinc-500 line-through">$250</span>
              </div>
              <div className="text-[11px] text-emerald-300 font-['JetBrains_Mono'] uppercase tracking-wider mb-6">
                80% off · open-ECU pricing
              </div>
              <ul className="space-y-2 mb-6 text-sm text-zinc-300 flex-1">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>Unlimited revisions until Green Light</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>AI Copilot scoring on every datalog</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>Stock .msq archived forever</span>
                </li>
              </ul>
              <button
                onClick={() => onCheckout('megasquirt')}
                disabled={isLoading}
                data-testid="megasquirt-checkout-btn"
                className="w-full py-3 rounded-sm font-bold text-xs tracking-[0.12em] uppercase transition-all flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-[#0A0A0A] hover:shadow-[0_0_30px_rgba(52,211,153,0.5)] disabled:opacity-60 disabled:cursor-wait"
              >
                {isLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                ) : (
                  <>Start MegaSquirt Tune — $50</>
                )}
              </button>
              <p className="text-[10px] text-zinc-500 mt-3 font-['JetBrains_Mono'] text-center">
                Send your .msq after checkout · email support@aitunerproportal.com
              </p>
            </motion.div>
          </div>

          {/* Bridge to the free diagnostic page — cheap conversion path for broken builds */}
          <div className="mt-10 text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-3 border border-emerald-500/30 bg-emerald-500/5 rounded-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-emerald-400">
                Build won't run?
              </span>
            </div>
            <p className="text-sm text-zinc-300 mb-3">
              Not ready for a full tune yet? Upload your .msq to our <span className="text-white font-bold">free diagnostic scanner</span> —
              20 sanity rules spot typos, wrong dead times, negative timing, empty VE cells, and more.
              Fix instructions are free. Pay <span className="text-[#FF5E1A] font-bold">$15–$35 per fix</span> or <span className="text-[#FF5E1A] font-bold">$100 flat for unlimited</span> to have us patch the file for you.
            </p>
            <a
              href="/megasquirt-diagnose"
              data-testid="megasquirt-diagnose-link"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-emerald-500/40 hover:border-emerald-400 bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-300 hover:text-emerald-200 text-xs font-bold tracking-[0.12em] uppercase rounded-sm transition-all"
            >
              Run the free diagnostic
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// =================== ROOT ===================
// Package keys that take a tune — these open the Mod Options dialog before Stripe.
// Hardware-only keys (datalogger_alone, autotuner, priority_addon) skip the dialog.
const TUNE_KEYS = new Set([
  'single', 'dual', 'shop',
  'datalogger_tune', 'datalogger_tune_lte',
  'ultimate', 'ultimate_lte',
  'combo_wifi', 'combo_lte',
  'megasquirt',
]);

// Friendly labels for the dialog header
const PACKAGE_LABELS = {
  single: { name: 'Single Tune', price: '$250' },
  dual: { name: 'Dual Tune', price: '$450' },
  shop: { name: 'Shop Pack', price: '$1,500' },
  datalogger_tune: { name: 'Datalogger + Tune (WiFi)', price: '$425' },
  datalogger_tune_lte: { name: 'Datalogger + Tune (LTE)', price: '$525' },
  ultimate: { name: 'Ultimate Bundle (WiFi)', price: '$675' },
  ultimate_lte: { name: 'Ultimate Bundle (LTE)', price: '$775' },
  combo_wifi: { name: 'Datalogger Combo (WiFi)', price: '$399' },
  combo_lte: { name: 'Datalogger Combo (LTE)', price: '$499' },
  megasquirt: { name: 'MegaSquirt Community Tune', price: '$50' },
};

export default function GreenLightTune({ onCTA }) {
  const [checkoutLoading, setCheckoutLoading] = useState(null); // packageKey currently redirecting
  const [paymentBanner, setPaymentBanner] = useState(null); // {type: 'success'|'cancelled', message}
  const [verifying, setVerifying] = useState(false);
  const [modDialog, setModDialog] = useState(null); // { packageKey } when open

  // Actual Stripe redirect — receives the already-selected packageKey + optional modOptions.
  const sendToStripe = useCallback(async (packageKey, modOptions) => {
    const packageId = PACKAGE_IDS[packageKey];
    if (!packageId) return;
    setCheckoutLoading(packageKey);
    try {
      const origin = `${window.location.origin}/green-light`;
      const headers = { 'Content-Type': 'application/json' };
      const token = (() => {
        try { return localStorage.getItem('auth_token'); } catch { return null; }
      })();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const body = { package_id: packageId, origin_url: origin };
      if (modOptions) body.mod_options = modOptions;
      const res = await fetch(`${API_URL}/api/checkout/session`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const detail = await res.text();
        console.error('[GreenLight] checkout failed:', res.status, detail);
        alert(`Could not start checkout (${res.status}). Please try again or email support@aitunerproportal.com.`);
        setCheckoutLoading(null);
        return;
      }
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Checkout failed — no redirect URL returned.');
        setCheckoutLoading(null);
      }
    } catch (err) {
      console.error('[GreenLight] checkout error:', err);
      alert('Network error starting checkout. Check your connection and try again.');
      setCheckoutLoading(null);
    }
  }, []);

  // Entry point wired to all pricing-card buttons. For tune packages, opens the
  // Mod Options dialog first. For hardware-only (datalogger_alone, autotuner,
  // priority_addon), skips straight to Stripe.
  const startCheckout = useCallback((packageKey) => {
    if (TUNE_KEYS.has(packageKey)) {
      setModDialog({ packageKey });
    } else {
      sendToStripe(packageKey);
    }
  }, [sendToStripe]);

  // Default CTA for hero / process / finalCTA buttons → Single Tune (most common path).
  const handleCTA = onCTA || (() => startCheckout('single'));

  // Set page title for SEO when this route renders
  useEffect(() => {
    const prev = document.title;
    document.title = 'Green Light Tune — Pay for Results, Not Attempts | AI Tuner Pro';
    return () => { document.title = prev; };
  }, []);

  // Detect Stripe redirect-back via query params: ?session_id=...&payment=success | ?payment=cancelled
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const sessionId = params.get('session_id');

    if (payment === 'cancelled') {
      setPaymentBanner({ type: 'cancelled', message: 'Checkout cancelled. No charge was made — try again whenever you\'re ready.' });
      // Clear the query params so reloading the page doesn't keep the banner
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if (payment === 'success' && sessionId) {
      setVerifying(true);
      // Poll the status endpoint a couple of times — Stripe sometimes lags by a second.
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const res = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
          if (res.ok) {
            const data = await res.json();
            if (data.payment_status === 'paid') {
              setPaymentBanner({
                type: 'success',
                message: `Payment received — your tune is in the queue. Check your email (${data.metadata?.email || 'on file'}) for next steps. We'll reach out within 24h to gather your binary + datalogs.`,
              });
              setVerifying(false);
              window.history.replaceState({}, '', window.location.pathname);
              return;
            }
          }
        } catch (err) {
          console.error('[GreenLight] payment verify error:', err);
        }
        if (attempts < 5) {
          setTimeout(poll, 1500);
        } else {
          setPaymentBanner({
            type: 'success',
            message: 'Thanks! Payment is processing. If you don\'t see a confirmation email within 5 minutes, contact support@aitunerproportal.com with your session ID.',
          });
          setVerifying(false);
          window.history.replaceState({}, '', window.location.pathname);
        }
      };
      poll();
    }
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="green-light-page">
      {/* Stripe redirect-back banner */}
      {(paymentBanner || verifying) && (
        <div
          data-testid="payment-banner"
          className={`fixed top-0 left-0 right-0 z-50 px-4 py-3 text-sm font-['JetBrains_Mono'] flex items-center justify-center gap-3 ${
            paymentBanner?.type === 'success'
              ? 'bg-emerald-600/95 text-white'
              : paymentBanner?.type === 'cancelled'
                ? 'bg-amber-600/95 text-white'
                : 'bg-[#FF5E1A]/95 text-white'
          }`}
        >
          {verifying && <Loader2 className="w-4 h-4 animate-spin" />}
          {paymentBanner?.type === 'success' && <CheckCircle2 className="w-4 h-4" />}
          {paymentBanner?.type === 'cancelled' && <AlertTriangle className="w-4 h-4" />}
          <span className="max-w-3xl text-center">
            {verifying ? 'Verifying your payment with Stripe…' : paymentBanner?.message}
          </span>
          {paymentBanner && !verifying && (
            <button
              onClick={() => setPaymentBanner(null)}
              data-testid="dismiss-payment-banner"
              className="ml-2 hover:opacity-70"
              aria-label="Dismiss"
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
      <GreenLightHero onCTA={handleCTA} />
      <ValueProps />
      <Process onCTA={handleCTA} />
      <Pricing onCTA={handleCTA} onCheckout={startCheckout} checkoutLoading={checkoutLoading} />
      <MegaSquirtSection onCheckout={startCheckout} checkoutLoading={checkoutLoading} />
      <Guarantee />
      <Comparison />
      <FAQ />
      <FinalCTA onCTA={handleCTA} />
      <GLFooter />

      {/* Mod Options dialog — intercepts tune checkouts so users can pick add-ons before Stripe */}
      <ModOptionsDialog
        open={!!modDialog}
        onClose={() => { if (!checkoutLoading) setModDialog(null); }}
        onConfirm={(modOptions) => {
          if (!modDialog) return;
          const pkg = modDialog.packageKey;
          setModDialog(null);
          sendToStripe(pkg, modOptions);
        }}
        packageName={modDialog ? (PACKAGE_LABELS[modDialog.packageKey]?.name || '') : ''}
        packagePrice={modDialog ? (PACKAGE_LABELS[modDialog.packageKey]?.price || '') : ''}
        submitting={!!checkoutLoading}
      />
    </div>
  );
}
